import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { emailAccountKeys } from '@/features/email-accounts/queries';
import { listActionItems, type ListActionItemsParams } from '@/lib/action-items';
import { useAuthStore } from '@/lib/stores/auth';

/** Rows per page. Matches the server default. */
export const ACTION_ITEMS_PAGE_SIZE = 25;

/**
 * Query options shared by the route loader (`ensureQueryData`) and the page, so
 * both address the identical cache entry.
 *
 * The key lives on `emailAccountKeys` — not a new top-level factory — so a
 * destructive account mutation's `invalidateQueries(all)` clears it, and
 * `propagateAccountUpdate` / the DNS-check mutation invalidate it by prefix.
 * Workspace-agnostic like every key there: the tenant-switch unfiltered
 * invalidate refetches it under the new workspace.
 */
export function actionItemsQueryOptions(params: ListActionItemsParams = {}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? ACTION_ITEMS_PAGE_SIZE;
  return {
    queryKey: emailAccountKeys.actionItems({ type: params.type ?? null, page, pageSize }),
    queryFn: () => listActionItems({ ...params, page, pageSize }),
    // A fix made on another page or in another tab should show up when the user
    // comes back — that is the whole point of the page.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  };
}

export function useActionItemsQuery(params: ListActionItemsParams = {}) {
  return useQuery({
    ...actionItemsQueryOptions(params),
    // Keep the previous page on screen while the next one loads, so flipping
    // filters/pages doesn't blank the list.
    placeholderData: keepPreviousData,
  });
}

/**
 * The sidebar's Action items badge. Rides the same key family as the page
 * (`emailAccountKeys.actionItems`), so every mutation that invalidates the
 * list refreshes the badge too; `pageSize: 1` keeps the payload to the
 * `total` / `truncated` it needs. Token-gated so unauthed frames fire nothing.
 */
export function useActionItemsCountQuery() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    ...actionItemsQueryOptions({ pageSize: 1 }),
    enabled: !!accessToken,
    // Limit alerts are org-level rows on the same page, so they count toward
    // the badge too. `total` respects the type filter, which this query never sets.
    select: (d) => ({ total: d.total + (d.limitAlerts?.length ?? 0), truncated: d.truncated }),
  });
}
